<template>
    <div class="wishlist-share-list">
        <template v-if="isSharedSuccessfully">
            <div class="wishlist-share-list__success" role="alert">
                <svg class="wishlist-share-list__success-icon">
                    <use xlink:href="#check-mark-icon-success"></use>
                </svg>
                <p>Your list was shared with {{ recepientEmail }}</p>
            </div>
        </template>
        <template v-else>
            <input             
                type="email" 
                name="recepientEmail" 
                v-model="recepientEmail" 
                placeholder="Recipient's Email"
                class="wishlist-share-list__recepient-email-box"
                required
            />

            <textarea             
                name="messageBody" 
                v-model="messageBody"
                :rows="isDesktopScreen ? 5 : 3"
                placeholder="Enter Message"
                class="wishlist-share-list__message-box"
                required
            />
            
            <button
                class="btn btn--secondary btn--medium wishlist-share-list__cta-save"
                :disabled="!isFormValid || isProcessing"
                @click="handleSendEmail"
            >
                <span :class="{'wishlist--processing': isProcessing}" class="wishlist__cta-loader">SHARE VIA EMAIL</span>
            </button>

            <div class="wishlist-share-list__cta-share-section">
                <button
                    class="btn wishlist-share-list__cta-send-text"
                    @click="handleSendText"
                >
                    Send Text
                </button>

                <TooltipModal
                    :customClass="isDesktopScreen ? `bottom` : `top`"
                    v-model="isLinkCopied"
                >
                    <template #tooltipContent>
                        <div class="tooltip-content" role="tooltip" aria-live="polite">
                            Link copied to clipboard!
                        </div>
                    </template>
                    <button
                        class="btn wishlist-share-list__cta-copy-link"
                        @click="handleCopyLink"
                    >
                        Copy Link
                    </button>
                </TooltipModal>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, ref, toRefs } from 'vue';
import { getSharedListURL, sendListToEmail } from '../../api';
import copy from 'copy-to-clipboard';
import TooltipModal from '../../../global/tooltip-modal.vue';

const props = defineProps({
    onCancel: {
        type: Function,
        default: () => { }
    },

    isDesktopScreen: {
        type: Boolean,
        default: true
    },

    selectedWishlistDetails: {
        type: Object,
        default: {}
    }
});

const { selectedWishlistDetails } = toRefs(props);

const isSharedSuccessfully = ref(false);
const isLinkCopied = ref(false);
const isProcessing = ref(false);

const recepientEmail = ref('');
const messageBody = ref('Hey there!\nCheckout my wishlist');

const isEmailValid = computed(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(recepientEmail.value);
});
const isFormValid = computed(() => isEmailValid.value && messageBody.value);

const handleCancelClick = () => {
    props.onCancel();
};

// Centralized function to fetch the shareable link
const fetchShareableLink = async () => {
    try {
        return await getSharedListURL(selectedWishlistDetails.value.lid);
    } catch (error) {
        console.log('Error while fetching shareable link', error);
        throw error;
    }
};

const handleCopyLink = async () => {
    try {
        const shareableLink = await fetchShareableLink();
        if (shareableLink) {
            copy(shareableLink)
            isLinkCopied.value = true;

            setTimeout(() => {
                isLinkCopied.value = false;
            }, 2000);
        }
    } catch {
        alert('Failed to copy link. Please try again.'); // User feedback
    }
};

const handleSendText = async () => {
    try {
        const shareableLink = await fetchShareableLink();
        if (shareableLink) {
            const smsUrl = `sms:?body=${encodeURIComponent(shareableLink)}`;
            window.location.href = smsUrl;
        }
    } catch {
        alert('Failed to send text. Please try again.'); // User feedback
    }
};

const handleSendEmail = async () => {
    try {
        isProcessing.value = true;
        const customerDetails = window?.getCustomerDetails?.();
        const response = await sendListToEmail(
            selectedWishlistDetails.value.lid,
            recepientEmail.value,
            customerDetails?.email,
            messageBody.value
        );
        if (response?.listinfo) {
            isSharedSuccessfully.value = true;
        }
    } catch {
        alert('Failed to send email. Please try again.'); // User feedback
    } finally {
        isProcessing.value = false;
    }
};
</script>
